import { formatDateTime } from '@pickleballinc/lib/format-dates';
import { useAppConfig } from '@pickleballinc/ui/components/global/ApplicationConfigProvider';
import { NoDataFound } from '@pickleballinc/ui/components/global/NoDataFound';
import clsx from 'clsx';
import { debounce } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import { useImpersonationBarHeight } from '@/hooks/useImpersonationBarHeight';
import { isValidDate } from '@/modules/results/helpers';
import { LogType } from '@/modules/results/logs';
import { allFilters } from '@/modules/results/match/filterList';
import { GamesData, Log, Player } from '@/modules/results/types';

interface MatchLogsProps {
  isMatchLive?: boolean;
  utcOffset: number;
  timezoneAbbreviation: string;
  playerList: Player[];
  logs: Log[];
}

type FiltersState = Record<string, boolean>;

const MatchLogs = ({
  logs,
  playerList,
  utcOffset,
  timezoneAbbreviation,
  isMatchLive,
}: MatchLogsProps) => {
  const [activeTab, setActiveTab] = useState<keyof GamesData>('SUMMARY');
  const [filtersStatus, setFiltersStatus] = useState(false);
  const [checkedAllFilters, setCheckedAllFilters] = useState(true);

  const targetRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const {
    config: { isImpersonationInProcess },
  } = useAppConfig();

  const barHeight = useImpersonationBarHeight();

  const checkVisibility = () => {
    const rect = targetRef.current?.getBoundingClientRect();
    if (rect) {
      const isInView = rect.bottom >= 0 && rect.top <= 0;
      setIsVisible(isInView);
    }
  };

  useEffect(() => {
    const debouncedCheckVisibility = debounce(checkVisibility, 20);
    window.addEventListener('scroll', debouncedCheckVisibility, { passive: true });
    debouncedCheckVisibility();
    return () => {
      window.removeEventListener('scroll', debouncedCheckVisibility);
      debouncedCheckVisibility.cancel();
    };
  }, []);

  const [gamesData, setGamesData] = useState<GamesData>({
    'PRE MATCH': [],
    'GAME 1': [],
    'GAME 2': [],
    'GAME 3': [],
    'GAME 4': [],
    'GAME 5': [],
    SUMMARY: [],
  });

  const [filters, setFilters] = useState<FiltersState>(
    allFilters.reduce((acc: FiltersState, filter) => {
      acc[filter.title2] = true;
      return acc;
    }, {})
  );

  // Toggle a specific filter based on 'title2'
  const toggleFilter = (filterKey: string) => {
    setFilters((prevFilters: FiltersState) => ({
      ...prevFilters,
      [filterKey]: !prevFilters[filterKey],
    }));
  };

  // Check all filters
  const checkAllFilters = () => {
    setCheckedAllFilters(true);
    setFilters(
      allFilters.reduce((acc: FiltersState, filter) => {
        acc[filter.title2] = true;
        return acc;
      }, {})
    );
  };

  // Uncheck all filters
  const uncheckAllFilters = () => {
    setCheckedAllFilters(false);
    setFilters(
      allFilters.reduce((acc: FiltersState, filter) => {
        acc[filter.title2] = false;
        return acc;
      }, {})
    );
  };

  const renderFilterSwitches = () => {
    return allFilters.map((filter) => (
      <div key={filter.title2} className='logs flex w-full items-center space-x-3 sm:w-1/4'>
        <label className='switch'>
          <input
            type='checkbox'
            checked={filters[filter.title2]}
            onChange={() => toggleFilter(filter.title2)}
            className='form-switch'
          />
          <span className='slider round' />
        </label>
        <span className='text-sm'>{filter.title1}</span>
      </div>
    ));
  };

  const getPlayerNameByUUID = (uuid: string): string => {
    const player = playerList.find((p) => p.uuid === uuid);
    return player ? player.name : 'Unknown';
  };

  const getTeamByUUID = (uuid: string): string => {
    const players = playerList.filter((p) => p.teamUuid === uuid);
    switch (players.length) {
      case 1:
        return players[0]?.name || 'End Change';
      case 2:
        return (players[0]?.name || 'End Change') + ' & ' + (players[1]?.name || 'End Change');
      default:
        return 'End Change';
    }
  };

  const formatLogTypeName = (logTypeName: string) => {
    return logTypeName
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b(\w)/g, (char: string) => char.toUpperCase());
  };

  const parseLogsData = (logsData: Log[]): GamesData => {
    const gamesDataTabs: GamesData = {
      'PRE MATCH': [],
      'GAME 1': [],
      'GAME 2': [],
      'GAME 3': [],
      'GAME 4': [],
      'GAME 5': [],
      SUMMARY: [],
    };
    let prevLog: Log = {
      log_index: 9999,
      log_type: 0,
      log_type_name: 'Undo',
      challenge_log: { team_uuid: '' },
      match_uuid: '',
    };
    if (logsData && playerList) {
      if (isMatchLive) {
        logsData.sort((a, b) => b?.log_index - a?.log_index);
      } else {
        logsData.sort(
          (a: { log_index: number }, b: { log_index: number }) => a.log_index - b.log_index
        );
      }

      logsData.forEach((log) => {
        const logTypeKey = log.log_type ? LogType[log.log_type] : 'UNDEFINED_LOG_REFERENCE_TYPE';

        // Check if the current log's type is checked in the filters
        if (logTypeKey && filters[logTypeKey]) {
          log.log_type_name = formatLogTypeName(LogType[log.log_type] || 'Unknown');

          // We assume log_type === 28 is 'CHECK_CONNECTION', which you may not want to display
          if (log.log_type !== 28 && log.note !== 'New game timeout') {
            if (log.log_type === 18 && prevLog.log_type === 12) {
              gamesDataTabs.SUMMARY.push({
                log_type: 24,
                log_type_name: 'Undo',
                log_index: 5555,
                challenge_log: { team_uuid: '' },
                match_uuid: log.match_uuid,
              });
              if (log.game_number) {
                const gameKey = `GAME ${log.game_number}`;
                if (Object.prototype.hasOwnProperty.call(gamesDataTabs, gameKey)) {
                  gamesDataTabs[gameKey as keyof GamesData].push({
                    log_type: 24,
                    log_type_name: 'Undo',
                    log_index: 999,
                    challenge_log: { team_uuid: '' },
                    match_uuid: log.match_uuid,
                  });
                }
              }
            }

            gamesDataTabs.SUMMARY.push(log);

            if (log.server_uuid) {
              log.server_name = log.log_type === 12 ? getPlayerNameByUUID(log.server_uuid) : '';
            }
            if (log.receiver_uuid) {
              log.receiver_name = log.log_type === 12 ? getPlayerNameByUUID(log.receiver_uuid) : '';
            }
            if (log.log_type === 18) {
              if (log.timeout_log) {
                log.server_name = getTeamByUUID(log.timeout_log.team_uuid);
                if (log.timeout_log.time_ended) {
                  log.log_type_name = 'Time In';
                  log.server_name = '';
                  log.receiver_name = '';
                }
              }
            }
            if (log.log_type === 7) {
              if (log.medical_timeout_log) {
                log.server_name = getPlayerNameByUUID(log.medical_timeout_log.player_uuid);
                if (log.medical_timeout_log.time_ended) {
                  log.log_type_name = 'Medical Time In';
                  log.server_name = '';
                  log.receiver_name = '';
                }
              }
            }
            if (log.log_type === 6) {
              log.end_score_current_game_string = '';
            }
            if (log.note && !log.game_number && log.log_type !== 18) {
              gamesDataTabs['PRE MATCH'].push(log);
            } else if (log.game_number) {
              const gameKey = `GAME ${log.game_number}`;
              if (Object.prototype.hasOwnProperty.call(gamesDataTabs, gameKey)) {
                gamesDataTabs[gameKey as keyof GamesData].push(log);
              }
            }
            if (log.note === 'refree correction') {
              log.note = 'Referee Correction';
            } else if (log.note === 'referee correction') {
              log.note = 'Referee Correction';
            }
            if (log.log_type === 2) {
              log.server_name = getTeamByUUID(log.challenge_log.team_uuid);
            }
          }
        }
        prevLog = log;
      });
    }
    return gamesDataTabs;
  };

  const renderTabs = (gamesDataVal: GamesData) => {
    return Object.keys(gamesDataVal)
      .filter((gameKey) => gamesDataVal[gameKey as keyof GamesData].length > 0)
      .map((gameKey) => (
        <div
          key={gameKey}
          onClick={() => setActiveTab(gameKey as keyof GamesData)}
          className={`min-w-[80px] cursor-pointer rounded-md border border-gray-300 p-2 text-center text-xs sm:text-sm md:min-w-[160px] ${
            activeTab === gameKey ? 'bg-experiment text-white' : 'bg-white text-black'
          }`}
        >
          {gameKey}
        </div>
      ));
  };

  const renderTableRows = (data: Log[], activeTab: keyof GamesData) => {
    if (!data.length)
      return (
        <div className='pt-9'>
          <NoDataFound title='No logs data found for this match!' />
        </div>
      );
    return data.map((log) => {
      const borderClass = log.log_type === 12 || log.log_type_name === 'Time Out' ? '' : 'border-b';
      const hideEndScoreForTypes = ['Point In Progress', 'Time Out'];

      const hideEndScore = log.log_type_name
        ? hideEndScoreForTypes.includes(log.log_type_name)
        : false;

      const modifiedLogDateCreated = log.date_created;

      if (
        activeTab === 'PRE MATCH' ||
        (activeTab === 'SUMMARY' && log.note && log.log_type !== 18)
      ) {
        return (
          <li key={crypto.randomUUID()}>
            <div className='flex gap-2 border-b py-3'>
              <div className='w-40'>
                {modifiedLogDateCreated && isValidDate(modifiedLogDateCreated)
                  ? formatDateTime(modifiedLogDateCreated, {
                      utcOffset: utcOffset,
                      timezoneAbbreviation: timezoneAbbreviation,
                      type: 'time',
                      precision: 'sec',
                    })
                  : ''}
              </div>
              <div className='flex-1'>{log.note}</div>
            </div>
          </li>
        );
      } else {
        return (
          <li key={crypto.randomUUID()}>
            <div className={clsx('flex gap-2 py-3', borderClass)}>
              <div className='w-40'>
                {modifiedLogDateCreated && isValidDate(modifiedLogDateCreated)
                  ? formatDateTime(modifiedLogDateCreated, {
                      utcOffset: utcOffset,
                      timezoneAbbreviation: timezoneAbbreviation,
                      type: 'time',
                      precision: 'sec',
                    })
                  : ''}
              </div>
              <div className='w-20'>{hideEndScore ? log.start_score_current_game_string : ''}</div>
              <div className='flex-1'>{log.server_name}</div>
              <div className='flex-1'>{log.receiver_name}</div>
              <div className='flex-1'>{log.log_type_name}</div>
              <div className='w-20'>{!hideEndScore ? log.end_score_current_game_string : ''}</div>
            </div>
          </li>
        );
      }
    });
  };

  useEffect(() => {
    const newGamesData = parseLogsData(logs);
    setGamesData(newGamesData);
  }, [logs, playerList, filters]);

  if (playerList.length === 0) return null;

  return (
    <div className='w-full'>
      <div className='container relative mx-auto py-3 sm:py-4 lg:px-4 2xl:px-0'>
        <div className='flex w-[340px] flex-nowrap justify-start gap-3 overflow-x-auto px-2 pb-3 sm:w-full sm:justify-center'>
          {renderTabs(gamesData)}
          <div
            onClick={() => setFiltersStatus(!filtersStatus)}
            className={`hidden min-w-[80px] cursor-pointer rounded-md border border-gray-300 ${
              filtersStatus ? 'bg-experiment text-white' : 'bg-white text-black'
            } bg-experiment p-2 text-center text-xs sm:block sm:text-sm md:min-w-[160px]`}
          >
            Filters
          </div>
        </div>
        <div
          onClick={() => setFiltersStatus(!filtersStatus)}
          className={`mx-auto mt-3 w-full cursor-pointer rounded-md border border-gray-300 ${
            filtersStatus ? 'bg-experiment text-white' : 'bg-white text-black'
          } p-2 text-center text-xs sm:hidden sm:text-sm md:min-w-[160px]`}
        >
          Filters
        </div>
        {filtersStatus ? (
          <div className='mt-5 border-t py-5'>
            <div className='flex gap-2 py-4'>
              <div
                onClick={checkAllFilters}
                className={`cursor-pointer rounded ${checkedAllFilters ? 'bg-experiment text-white' : 'bg-white text-black'}  border px-4 py-2`}
              >
                Check All
              </div>
              <div
                onClick={uncheckAllFilters}
                className={`cursor-pointer rounded ${!checkedAllFilters ? 'bg-experiment text-white' : 'bg-white text-black'}  border px-4 py-2`}
              >
                Uncheck All
              </div>
            </div>
            <div className='flex flex-wrap gap-y-3 py-4'>{renderFilterSwitches()}</div>
          </div>
        ) : null}
      </div>
      {isVisible ? (
        <div
          style={{
            position: 'fixed',
            top: isImpersonationInProcess ? barHeight : 0,
            left: 0,
            width: '100%',
            zIndex: 100,
          }}
        >
          <div className='overflow-x-auto'>
            <div className='min-w-[800px]'>
              <div className='w-full'>
                <div className='bg-black'>
                  <div className='container mx-auto text-white lg:px-4 2xl:px-0'>
                    <div className='flex gap-2 py-3'>
                      <div className='w-40'>Time</div>
                      <div className='w-20'>Start</div>
                      <div className='flex-1'>Server</div>
                      <div className='flex-1'>Receiver</div>
                      <div className='flex-1'>Result</div>
                      <div className='w-20'>End</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <div className='overflow-x-auto'>
        <div className='min-w-[800px]'>
          <div className='w-full'>
            <div className='bg-black'>
              <div className='container mx-auto text-white lg:px-4 2xl:px-0'>
                <div className='flex gap-2 py-3'>
                  <div className='w-40'>Time</div>
                  <div className='w-20'>Start</div>
                  <div className='flex-1'>Server</div>
                  <div className='flex-1'>Receiver</div>
                  <div className='flex-1'>Result</div>
                  <div className='w-20'>End</div>
                </div>
              </div>
            </div>
            <div className='container mx-auto lg:px-4 2xl:px-0' ref={targetRef}>
              <ul>{renderTableRows(gamesData[activeTab], activeTab)}</ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchLogs;
